/**
 * Lo que dejó el modo simulado al pasar a real (C4 `restos-simulados`) y el rebase del saldo. Puro.
 *
 * Con el servidor en twilio desaparece el banner «Modo simulado» y los restos quedan desparramados
 * con chips chicos: la tarjeta de Configuración los junta con su cantidad, y la de Saldo dice adónde
 * fue la plata de prueba (el saldo en 0 sin explicación hacía creer que se perdió).
 *
 * Los textos dicen exactamente lo que hace la limpieza. De «no contactar» levanta SÓLO las bajas con
 * la marca `simulado: true`; una baja del canal oficial sin la marca no se levanta nunca sola (Ley
 * 25.326: en la duda, la baja se queda) y se avisa aparte para revisarla a mano.
 */

import type { RebaseSaldo, RestosSimulados, ResultadoLimpiezaRestos } from "../types";
import { fechaHora, num, plural } from "../../campana-nueva/utils/format";
import { usd } from "./formato";

const n = (x: unknown) => Number(x) || 0;

/** Qué hace «Limpiar restos del simulado», y qué no. Lo muestran la tarjeta y el modal de confirmación. */
export const TEXTO_QUE_HACE_LIMPIEZA =
  "Archiva las plantillas simuladas, cancela las campañas simuladas que siguen abiertas, levanta de «no contactar» sólo las bajas marcadas como simuladas, saca del saldo los movimientos simulados que todavía suman y limpia la cuenta: borra los datos del simulador, vuelve al configurado el ritmo que bajó el simulador y levanta la marca frenada por el simulador. No borra el historial (los mensajes, los movimientos y las respuestas quedan, fuera del saldo y de las estadísticas), no cambia los borradores reales y no toca ninguna baja sin marca: ésas se revisan a mano en Bajas.";

/**
 * Una línea por cada resto que hay, con su cantidad. Lo que no hay no aparece. Primero lo que la
 * limpieza arregla (plantillas, campañas, bajas marcadas, la cuenta, la marca frenada), después los
 * borradores reales (la limpieza no los toca: hay que cambiarles la plantilla) y al final la historia
 * que queda a la vista (mensajes, movimientos, respuestas). Las bajas sin marca no van acá: no son
 * restos que se limpien (`textoBajasSinMarca`).
 */
export function lineasRestos(r: RestosSimulados | null | undefined): string[] {
  if (!r) return [];
  const l: string[] = [];
  const pl = n(r.plantillas?.total);
  if (pl > 0) {
    const ap = n(r.plantillas?.aprobadas);
    l.push(`${num(pl)} ${plural(pl, "plantilla simulada", "plantillas simuladas")}${ap > 0 ? ` (${num(ap)} ${plural(ap, "aprobada", "aprobadas")})` : ""}`);
  }
  const ab = n(r.campanas?.abiertas);
  if (ab > 0) l.push(`${num(ab)} ${plural(ab, "campaña simulada abierta", "campañas simuladas abiertas")}`);
  const dnc = n(r.noContactarSimuladas);
  if (dnc > 0) l.push(`${num(dnc)} ${plural(dnc, "baja marcada", "bajas marcadas")} como del modo simulado en «no contactar»`);
  if (r.cuentaConRestos) l.push("La cuenta guarda datos del número o confirmaciones del simulado");
  if (r.marcaFrenadaSimulada === true) l.push("La marca quedó frenada por un error de un mensaje simulado");
  const bo = n(r.campanas?.borradoresConPlantillaSimulada);
  if (bo > 0) {
    l.push(`${num(bo)} ${plural(bo,
      "borrador real con una plantilla simulada: la limpieza no lo toca, cambiale la plantilla",
      "borradores reales con una plantilla simulada: la limpieza no los toca, cambiales la plantilla")}`);
  }
  const me = n(r.mensajes);
  if (me > 0) l.push(`${num(me)} ${plural(me, "mensaje simulado", "mensajes simulados")}`);
  const mo = n(r.movimientos?.total);
  if (mo > 0) {
    const fuera = n(r.movimientos?.fueraDelSaldo);
    const suman = Math.max(0, mo - fuera);
    const detalle: string[] = [];
    if (fuera > 0) detalle.push(`${num(fuera)} fuera del saldo`);
    if (suman > 0) detalle.push(`${num(suman)} todavía ${plural(suman, "suma", "suman")} al saldo`);
    l.push(`${num(mo)} ${plural(mo, "movimiento simulado", "movimientos simulados")}${detalle.length ? ` (${detalle.join(", ")})` : ""}`);
  }
  const re = n(r.respuestas);
  if (re > 0) l.push(`${num(re)} ${plural(re, "respuesta simulada", "respuestas simuladas")}`);
  return l;
}

/**
 * ¿Hay algo que la limpieza haga? Sólo con esto se ofrece el botón: los borradores reales con una
 * plantilla simulada y las bajas sin marca también se muestran, pero la limpieza no los toca, y un
 * botón que contesta «No había nada para limpiar» hace creer que se arreglaron.
 */
export function hayQueLimpiar(r: RestosSimulados | null | undefined): boolean {
  if (!r) return false;
  return n(r.plantillas?.total) > 0
    || n(r.campanas?.abiertas) > 0
    || n(r.noContactarSimuladas) > 0
    || r.cuentaConRestos === true
    || r.marcaFrenadaSimulada === true
    || n(r.movimientos?.total) > n(r.movimientos?.fueraDelSaldo);
}

/**
 * «3 bajas viejas del canal oficial sin origen: revisalas a mano en No contactar. La limpieza no las
 * toca.» null si no hay. Son bajas que el canal oficial escribió antes de la marca `simulado`: no se
 * sabe si las pidió una persona, así que se quedan hasta que alguien las mire.
 */
export function textoBajasSinMarca(x: number | null | undefined): string | null {
  const k = n(x);
  if (k <= 0) return null;
  if (k === 1) return "1 baja vieja del canal oficial sin origen: revisala a mano en Bajas. La limpieza no la toca.";
  return `${num(k)} bajas viejas del canal oficial sin origen: revisalas a mano en Bajas. La limpieza no las toca.`;
}

/**
 * «El 24/09 09:28 se sacaron USD 92,95 de 124 movimientos simulados al pasar al modo real: el saldo pasó
 * de USD 92,95 a USD 0,00.» null sin rebase. Si el saldo simulado había quedado negativo, el rebase
 * suma: se dice «se devolvieron».
 */
export function textoRebase(r: RebaseSaldo | null | undefined): string | null {
  if (!r) return null;
  const antes = n(r.antesUSD);
  const despues = n(r.despuesUSD);
  const diferencia = antes - despues;
  const verbo = diferencia >= 0 ? "se sacaron" : "se devolvieron";
  const ex = n(r.excluidos);
  // «al modo real», no «a Twilio»: la frase sale también en «Saldo y costos», que lee el comercio.
  const cuando = r.at ? `El ${fechaHora(r.at)} ` : "Al pasar al modo real ";
  return `${cuando}${verbo} ${usd(Math.abs(diferencia))} de ${num(ex)} ${plural(ex, "movimiento simulado", "movimientos simulados")} al pasar al modo real: el saldo pasó de ${usd(antes)} a ${usd(despues)}.`;
}

/**
 * Qué hizo «Limpiar restos del simulado», en una frase («Se archivó 1 plantilla, se canceló 1
 * campaña…»). Lo que `acciones` no cuenta sale de comparar `antes` con `despues`: los movimientos que
 * salieron del saldo y la marca frenada por el simulador que se levantó.
 */
export function textoLimpieza(
  a: ResultadoLimpiezaRestos["acciones"] | null | undefined,
  r?: Partial<Pick<ResultadoLimpiezaRestos, "antes" | "despues">> | null,
): string {
  if (!a && !r) return "Listo.";
  const partes: string[] = [];
  const pl = n(a?.plantillasArchivadas);
  if (pl > 0) partes.push(pl === 1 ? "se archivó 1 plantilla" : `se archivaron ${num(pl)} plantillas`);
  const ca = n(a?.campanasCanceladas);
  if (ca > 0) partes.push(ca === 1 ? "se canceló 1 campaña" : `se cancelaron ${num(ca)} campañas`);
  const dnc = n(a?.noContactarLevantadas);
  if (dnc > 0) {
    partes.push(dnc === 1 ? "se levantó 1 baja marcada como simulada de «no contactar»" : `se levantaron ${num(dnc)} bajas marcadas como simuladas de «no contactar»`);
  }
  const antes = r?.antes;
  const despues = r?.despues;
  const sacados = antes && despues ? n(despues.movimientos?.fueraDelSaldo) - n(antes.movimientos?.fueraDelSaldo) : 0;
  if (sacados > 0) partes.push(sacados === 1 ? "se sacó del saldo 1 movimiento simulado" : `se sacaron del saldo ${num(sacados)} movimientos simulados`);
  if (a?.cuentaLimpia) partes.push("se borraron de la cuenta los datos del simulado");
  if (antes?.marcaFrenadaSimulada === true && despues && despues.marcaFrenadaSimulada !== true) partes.push("se levantó la marca frenada por el simulador");
  if (!partes.length) return "No había nada para limpiar.";
  const frase = partes.length === 1 ? partes[0] : `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}`;
  return `${frase.charAt(0).toUpperCase()}${frase.slice(1)}.`;
}
